import { useEffect, useMemo, useState } from "react";
import { api, getProductionId } from "@/lib/api";
import { toast } from "sonner";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { CheckCircle2, Circle, Loader2, User2, Trash2, ClipboardCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/components/ConfirmDialog";
import { fmtDate } from "@/lib/format";

export default function Actionables({ embedded = false }) {
  const { user } = useAuth();
  const confirm = useConfirm();
  const canModify = ["org_admin", "production_staff", "super_admin"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [scope, setScope] = useState("mine"); // mine | all
  const [status, setStatus] = useState("open");
  const [loading, setLoading] = useState(true);
  const productionId = getProductionId();

  const load = async () => {
    setLoading(true);
    try {
      const params = { production_id: productionId };
      if (scope === "mine") params.mine = true;
      if (status !== "all") params.status = status;
      const { data } = await api.get("/actionables", { params });
      setItems(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [scope, status]);

  const grouped = useMemo(() => {
    const g = { open: [], in_progress: [], done: [] };
    items.forEach((a) => { (g[a.status] || g.open).push(a); });
    return g;
  }, [items]);

  const setStatusOf = async (a, next) => {
    try {
      await api.patch(`/actionables/${a.id}`, { status: next });
      toast.success(`Marked ${next.replace("_", " ")}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };
  const del = async (id) => {
    const ok = await confirm({ title: "Delete this actionable?", body: "This will remove the follow-up task from the list.", confirmLabel: "Delete", tone: "danger" });
    if (!ok) return;
    await api.delete(`/actionables/${id}`);
    load();
  };

  return (
    <div className={embedded ? "space-y-4" : "space-y-6"} data-testid="actionables-page">
      {!embedded && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-gold mb-1">Follow-ups</div>
          <h1 className="font-display text-4xl sm:text-5xl">Actionables</h1>
          <p className="text-zinc-500 text-sm mt-2">Every task tagged to you or the team from rehearsal reports.</p>
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1.5">
          {[
            { k: "mine", label: "Mine" },
            canModify && { k: "all", label: "Everyone" },
          ].filter(Boolean).map(({ k, label }) => (
            <button key={k} onClick={() => setScope(k)} data-testid={`scope-${k}`}
              className={`px-3 py-1.5 rounded-full text-xs border ${scope === k ? "bg-gold text-black border-gold font-semibold" : "border-white/15 text-zinc-300 hover:bg-white/5"}`}>{label}</button>
          ))}
        </div>
        <div className="w-px h-4 bg-white/10 mx-1" />
        <div className="flex items-center gap-1.5">
          {["open", "in_progress", "done", "all"].map((k) => (
            <button key={k} onClick={() => setStatus(k)} data-testid={`status-${k}`}
              className={`px-3 py-1.5 rounded-full text-xs border ${status === k ? "bg-white text-black border-white font-semibold" : "border-white/15 text-zinc-300 hover:bg-white/5"}`}>{k.replace("_", " ")}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-zinc-500"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-white/10 p-12 text-center text-zinc-500">
          <ClipboardCheck className="w-8 h-8 mx-auto mb-3 text-zinc-700" />
          Nothing here. New actionables get added from rehearsal reports.
        </div>
      ) : (
        <div className="space-y-6">
          {["open", "in_progress", "done"].map((s) => (
            grouped[s].length > 0 && (
              <div key={s}>
                <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 mb-2">{s.replace("_", " ")} ({grouped[s].length})</div>
                <div className="space-y-2">
                  {grouped[s].map((a) => (
                    <ActionableRow key={a.id} a={a} onStatus={setStatusOf} onDelete={del} canModify={canModify} />
                  ))}
                </div>
              </div>
            )
          ))}
        </div>
      )}
    </div>
  );
}

function ActionableRow({ a, onStatus, onDelete, canModify }) {
  const next = a.status === "done" ? "open" : a.status === "open" ? "in_progress" : "done";
  const StatusIcon = a.status === "done" ? CheckCircle2 : Circle;
  return (
    <div data-testid={`actionable-row-${a.id}`} className="rounded-lg border border-white/10 bg-[#0F0F0F] p-4 flex items-start gap-3">
      <button onClick={() => onStatus(a, next)} className="shrink-0 mt-0.5" title={`Move to ${next.replace("_", " ")}`}>
        <StatusIcon className={`w-5 h-5 ${a.status === "done" ? "text-emerald-400" : a.status === "in_progress" ? "text-amber-400" : "text-zinc-500 hover:text-white"}`} />
      </button>
      <div className="flex-1 min-w-0">
        <div className={`text-sm ${a.status === "done" ? "line-through text-zinc-500" : "text-zinc-100"}`}>{a.description}</div>
        <div className="mt-1 text-[11px] text-zinc-500 flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1"><User2 className="w-3 h-3" /> {a.assigned_to_name || "Unassigned"}</span>
          {a.rehearsal_title && <span>· {a.rehearsal_title} · {fmtDate(a.rehearsal_date)}</span>}
          {a.assigned_by_name && <span>· by {a.assigned_by_name}</span>}
        </div>
      </div>
      {canModify && (
        <button onClick={() => onDelete(a.id)} className="text-zinc-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
      )}
    </div>
  );
}
